"use client";

import { c as _c } from "react/compiler-runtime";
import { useRecommendations } from "@/hooks/use-recommendations";
import { RecommendedResults } from "./recommended-results";
import { BrowseNavigation } from "./browse-navigation";
import { BrowseAllResults } from "./browse-all-results";
import { Suspense, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { GenreSidebar, type TagData } from "@/components/browse/genre-sidebar";
import { FilterShell } from "@/components/browse/filter-shell";
import { FilterEmptyState } from "@/components/browse/filter-empty-state";
import { TrendingCarousel } from "@/components/browse/trending-carousel";
import { StoryGrid } from "@/components/browse/story-grid";
import { filterCatalogStories } from "@/lib/catalog-filter";
import { mergeGenreSelection, parseDiscovery, parseGenres, parseStatus, parseTags, renderFilterUrl } from "@/lib/filter-utils";
import type { CatalogStory, StoryGenre, StoryStatus } from "@/lib/actions/story-actions";
import { crownDiscoveryFilterOption, type CrownDiscoveryFilter } from "@/lib/spotlights/discovery-filters";
import type { SpotlightDiscoveryData, SpotlightDiscoveryResult } from "@/lib/spotlights/queries";

// ─── Catalog explorer ────────────────────────────────────
// The filterable half of the browse/genre pages. The pages themselves are
// statically rendered (ISR) and embed the FULL public catalog; this client
// component reads the filter state from the URL and filters in memory
// (catalog-filter.ts mirrors the SQL semantics), so neither bot crawls nor
// human filter clicks invoke a server function.
//
// useSearchParams would normally bail the whole static page out of
// prerendering, so it's quarantined inside the Suspense boundary below: the
// fallback renders the UNFILTERED view (which is what belongs in the static
// HTML — that's also the SEO content), and the hydrated client swaps in the
// filtered view. For clean URLs the two are identical, so nothing changes
// visually.

interface ParsedFilters {
  genres: StoryGenre[];
  discovery?: CrownDiscoveryFilter;
  status?: StoryStatus;
  tags: string[];
  q?: string;
}
const NO_FILTERS: ParsedFilters = {
  genres: [],
  tags: []
};
const EMPTY_CROWN_DISCOVERY: SpotlightDiscoveryData = {
  mostSpotlighted: [],
  ascending: [],
  freshFinds: []
};
const DISABLED_CROWN_DISCOVERY: SpotlightDiscoveryResult = {
  status: "disabled"
};
interface CatalogExplorerProps {
  recommendedIds?: string[];
  personalized?: boolean;
  variant: "browse" | "genre";
  /** Full public catalog with facets, embedded at ISR time. */
  stories: CatalogStory[];
  /** Sidebar tag list. */
  tags: TagData[];
  /** Ranked Crown category membership for Browse discovery filters. */
  crownDiscovery?: SpotlightDiscoveryResult;
  /** genre variant only: the page's path genre. */
  anchorGenre?: StoryGenre;
  /** genre variant only: the path genre's slug, for clear-filter hrefs. */
  anchorSlug?: string;
}
export function CatalogExplorer(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<CatalogView {...props} filters={NO_FILTERS} />}><CatalogWithParams {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** Reads the live URL filter state — only rendered client-side. */
function CatalogWithParams(props) {
  const $ = _c(34);
  const sp = useSearchParams();
  const router = useRouter();
  const crownDiscoveryAvailable = props.crownDiscovery?.status === "enabled";
  let rawDiscovery;
  let t0;
  if ($[0] !== sp) {
    rawDiscovery = sp.get("discovery");
    t0 = parseDiscovery(rawDiscovery ?? undefined);
    $[0] = sp;
    $[1] = rawDiscovery;
    $[2] = t0;
  } else {
    rawDiscovery = $[1];
    t0 = $[2];
  }
  const parsedDiscovery = t0;
  let t1;
  if ($[3] !== sp) {
    t1 = sp.get("q")?.trim() || undefined;
    $[3] = sp;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const q = t1;
  let t2;
  if ($[5] !== sp) {
    t2 = parseGenres(sp.getAll("genre"));
    $[5] = sp;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = props.variant === "browse" && crownDiscoveryAvailable ? parsedDiscovery : undefined;
  let t4;
  if ($[7] !== sp) {
    t4 = parseStatus(sp.get("status") ?? undefined);
    $[7] = sp;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== sp) {
    t5 = parseTags(sp.getAll("tag"));
    $[9] = sp;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== q || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      genres: t2,
      discovery: t3,
      status: t4,
      tags: t5,
      q
    };
    $[11] = q;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const filters = t6;
  let t7;
  if ($[17] !== sp) {
    t7 = sp.get("view");
    $[17] = sp;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const browseAll = t7 === "all";
  const hasFilters = Boolean(filters.q || filters.status || filters.discovery || filters.genres.length || filters.tags.length);
  const personal = useRecommendations(props.variant === "browse" && props.recommendedIds !== undefined && !browseAll && !hasFilters);
  const variant = props.variant;
  let t8;
  let t9;
  if ($[19] !== crownDiscoveryAvailable || $[20] !== parsedDiscovery || $[21] !== q || $[22] !== rawDiscovery || $[23] !== router || $[24] !== sp || $[25] !== variant) {
    t8 = () => {
      if (variant !== "browse") {
        return;
      }
      const genres = parseGenres(sp.getAll("genre"));
      const removeDiscovery = rawDiscovery !== null && (!crownDiscoveryAvailable || !parsedDiscovery);
      if (genres.length === 0 && !removeDiscovery) {
        return;
      }
      const target = renderFilterUrl({
        view: sp.get("view") === "all" ? "all" : undefined,
        genres,
        discovery: removeDiscovery ? undefined : parsedDiscovery,
        status: parseStatus(sp.get("status") ?? undefined),
        tags: parseTags(sp.getAll("tag")),
        q
      });
      router.replace(target);
    };
    t9 = [crownDiscoveryAvailable, parsedDiscovery, q, rawDiscovery, router, sp, variant];
    $[19] = crownDiscoveryAvailable;
    $[20] = parsedDiscovery;
    $[21] = q;
    $[22] = rawDiscovery;
    $[23] = router;
    $[24] = sp;
    $[25] = variant;
    $[26] = t8;
    $[27] = t9;
  } else {
    t8 = $[26];
    t9 = $[27];
  }
  useEffect(t8, t9);
  const t10 = personal?.ids ?? props.recommendedIds;
  const t11 = Boolean(personal);
  let t12;
  if ($[28] !== browseAll || $[29] !== filters || $[30] !== props || $[31] !== t10 || $[32] !== t11) {
    t12 = <CatalogView {...props} recommendedIds={t10} personalized={t11} filters={filters} browseAll={browseAll} />;
    $[28] = browseAll;
    $[29] = filters;
    $[30] = props;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}

/** Pure presentational render of one filter state — SSR-safe. */
function CatalogView(t0) {
  const $ = _c(67);
  const {
    variant,
    stories,
    tags,
    anchorGenre,
    anchorSlug,
    crownDiscovery: t1,
    filters,
    recommendedIds,
    personalized: t2,
    browseAll: t3
  } = t0;
  const crownDiscovery = t1 === undefined ? DISABLED_CROWN_DISCOVERY : t1;
  const personalized = t2 === undefined ? false : t2;
  const browseAll = t3 === undefined ? false : t3;
  if (variant === "genre" && anchorGenre) {
    let T0;
    let filtered;
    let hasActiveFilters;
    let t4;
    let t5;
    let t6;
    if ($[0] !== anchorGenre || $[1] !== browseAll || $[2] !== filters.genres || $[3] !== filters.q || $[4] !== filters.status || $[5] !== filters.tags || $[6] !== stories || $[7] !== tags) {
      let t7;
      if ($[14] !== anchorGenre) {
        t7 = g => g !== anchorGenre;
        $[14] = anchorGenre;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      const extraGenres = filters.genres.filter(t7);
      const activeGenres = mergeGenreSelection(anchorGenre, extraGenres);
      filtered = filterCatalogStories(stories, {
        genres: activeGenres,
        status: filters.status,
        tags: filters.tags,
        q: filters.q
      });
      let t8;
      if ($[16] !== filters.q || $[17] !== filters.status || $[18] !== filters.tags || $[19] !== stories) {
        t8 = filterCatalogStories(stories, {
          status: filters.status,
          tags: filters.tags,
          q: filters.q
        });
        $[16] = filters.q;
        $[17] = filters.status;
        $[18] = filters.tags;
        $[19] = stories;
        $[20] = t8;
      } else {
        t8 = $[20];
      }
      const totalCount = t8.length;
      hasActiveFilters = Boolean(filters.status) || filters.tags.length > 0 || extraGenres.length > 0 || Boolean(filters.q);
      const activeFilterCount = activeGenres.length + filters.tags.length + (filters.status ? 1 : 0);
      const t9 = <FilterShell activeCount={activeFilterCount}><GenreSidebar activeView={browseAll ? "all" : undefined} storyCount={totalCount} activeGenres={activeGenres} activeStatus={filters.status} activeTags={filters.tags} activeQuery={filters.q} anchorGenre={anchorGenre} tags={tags} /></FilterShell>;
      if ($[21] !== t9) {
        t6 = <div className="md:col-start-1 md:row-start-1 md:row-span-2">{t9}</div>;
        $[21] = t9;
        $[22] = t6;
      } else {
        t6 = $[22];
      }
      t5 = "md:col-start-2 md:row-start-2 min-w-0 flex flex-col gap-8";
      T0 = TrendingCarousel;
      t4 = filtered.slice(0, 5);
      $[0] = anchorGenre;
      $[1] = browseAll;
      $[2] = filters.genres;
      $[3] = filters.q;
      $[4] = filters.status;
      $[5] = filters.tags;
      $[6] = stories;
      $[7] = tags;
      $[8] = T0;
      $[9] = filtered;
      $[10] = hasActiveFilters;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
    } else {
      T0 = $[8];
      filtered = $[9];
      hasActiveFilters = $[10];
      t4 = $[11];
      t5 = $[12];
      t6 = $[13];
    }
    let t7;
    if ($[23] !== T0 || $[24] !== t4) {
      t7 = <T0 stories={t4} />;
      $[23] = T0;
      $[24] = t4;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== anchorSlug || $[27] !== browseAll || $[28] !== filtered || $[29] !== hasActiveFilters) {
      t8 = filtered.length === 0 && hasActiveFilters ? <FilterEmptyState clearHref={`/genre/${anchorSlug}${browseAll ? "?view=all" : ""}`} title="No novels match your filters." message="Try removing a genre, tag, or status filter to widen your results." /> : <StoryGrid stories={filtered} />;
      $[26] = anchorSlug;
      $[27] = browseAll;
      $[28] = filtered;
      $[29] = hasActiveFilters;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== t5 || $[32] !== t7 || $[33] !== t8) {
      t9 = <section className={t5}>{t7}{t8}</section>;
      $[31] = t5;
      $[32] = t7;
      $[33] = t8;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] !== t6 || $[36] !== t9) {
      t10 = <>{t6}{t9}</>;
      $[35] = t6;
      $[36] = t9;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    return t10;
  }
  const crownDiscoveryAvailable = crownDiscovery.status === "enabled";
  const crownDiscoveryData = crownDiscoveryAvailable ? crownDiscovery.data : EMPTY_CROWN_DISCOVERY;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[38] !== browseAll || $[39] !== crownDiscoveryAvailable || $[40] !== crownDiscoveryData || $[41] !== filters.discovery || $[42] !== filters.q || $[43] !== filters.status || $[44] !== filters.tags || $[45] !== personalized || $[46] !== recommendedIds || $[47] !== stories || $[48] !== tags) {
    const filtered_0 = filterCatalogStories(stories, {
      discovery: filters.discovery,
      status: filters.status,
      tags: filters.tags,
      q: filters.q
    }, crownDiscoveryData);
    let t8;
    if ($[53] !== filters.discovery) {
      t8 = filters.discovery ? crownDiscoveryFilterOption(filters.discovery) : undefined;
      $[53] = filters.discovery;
      $[54] = t8;
    } else {
      t8 = $[54];
    }
    const discoveryOption = t8;
    const activeFilterCount_0 = filters.tags.length + (filters.status ? 1 : 0) + (filters.discovery ? 1 : 0);
    const hasActiveFilters_0 = activeFilterCount_0 > 0 || Boolean(filters.q);
    t7 = <FilterShell activeCount={activeFilterCount_0}><GenreSidebar activeView={browseAll ? "all" : undefined} storyCount={filtered_0.length} activeDiscovery={filters.discovery} activeStatus={filters.status} activeTags={filters.tags} activeQuery={filters.q} showDiscoveryFilters={crownDiscoveryAvailable} tags={tags} /></FilterShell>;
    t4 = "flex-1 min-w-0 flex flex-col gap-8";
    const t9 = recommendedIds !== undefined;
    const t10 = browseAll || recommendedIds === undefined;
    if ($[55] !== hasActiveFilters_0 || $[56] !== personalized || $[57] !== t10 || $[58] !== t9) {
      t5 = <BrowseNavigation recommendationsAvailable={t9} personalized={personalized} browseAll={t10} filtered={hasActiveFilters_0} />;
      $[55] = hasActiveFilters_0;
      $[56] = personalized;
      $[57] = t10;
      $[58] = t9;
      $[59] = t5;
    } else {
      t5 = $[59];
    }
    t6 = browseAll && !hasActiveFilters_0 ? <BrowseAllResults stories={stories} /> : recommendedIds && !hasActiveFilters_0 ? <RecommendedResults stories={filtered_0} ids={recommendedIds} personalized={personalized} /> : <>{!filters.discovery && <TrendingCarousel stories={filtered_0.slice(0, 5)} />}{filtered_0.length === 0 && hasActiveFilters_0 ? <FilterEmptyState clearHref={browseAll ? "/browse?view=all" : "/browse"} title={filters.q ? `No novels match “${filters.q}”.` : "No novels match your filters."} message={filters.q ? "Try a different title or author, or clear your search." : filters.discovery ? "Try another Crown discovery filter or clear your filters." : "Try removing a tag or status filter to widen your results."} clearLabel={filters.q ? "Clear search \u2192" : "Clear filters \u2192"} /> : <StoryGrid stories={filtered_0} title={discoveryOption?.label} source={discoveryOption?.source} sortLabel={discoveryOption?.shortLabel} />}</>;
    $[38] = browseAll;
    $[39] = crownDiscoveryAvailable;
    $[40] = crownDiscoveryData;
    $[41] = filters.discovery;
    $[42] = filters.q;
    $[43] = filters.status;
    $[44] = filters.tags;
    $[45] = personalized;
    $[46] = recommendedIds;
    $[47] = stories;
    $[48] = tags;
    $[49] = t4;
    $[50] = t5;
    $[51] = t6;
    $[52] = t7;
  } else {
    t4 = $[49];
    t5 = $[50];
    t6 = $[51];
    t7 = $[52];
  }
  let t8;
  if ($[60] !== t4 || $[61] !== t5 || $[62] !== t6) {
    t8 = <section className={t4}>{t5}{t6}</section>;
    $[60] = t4;
    $[61] = t5;
    $[62] = t6;
    $[63] = t8;
  } else {
    t8 = $[63];
  }
  let t9;
  if ($[64] !== t7 || $[65] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[64] = t7;
    $[65] = t8;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  return t9;
}